import {
  Box, Button, Center, Flex, HStack,
  Icon, IconButton, Input, InputGroup,
  InputLeftElement, InputRightElement, Tag,
  TagLabel,
  Tooltip,
  useDisclosure,
  VStack
} from '@chakra-ui/react'
import { InfoIcon } from '@chakra-ui/icons'
import { isEmpty, isEqual } from 'lodash'
import { stringify } from 'query-string'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import { IoSearch } from 'react-icons/io5'
import { TbPhotoSearch, TbX } from 'react-icons/tb'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { colors } from '../../../../ui-kit'
import { FeaturesEnum } from '../../../../utils/features'
import {
  fileTypeOptionsMapping,
  getSearchQueryFromJSON,
  validateFilter
} from '../../../../utils/mediaLibrary'
import { canonicalize, normalizeSearchUrlForComparison } from '../../../../utils/search'
import { FileType, MLSortDirection, MLSortParams } from '../../../constants'
import type {
  CustomMetadataResponse,
  FileTypeStateObject,
  FormValidationObject,
  SearchQueryJson
} from '../../../interfaces'
import { MLContext, NavBarContext, UserContext } from '../../../providers'
import { CustomMetadataContext } from '../../../providers/CustomMetadataProvider'
import { MediaLibraryActions } from '../../../providers/MLProvider'
import SaveSearchModalNew from '../../SavedSearch/SaveSearchModalNew'
import AdvancedSearch from '../AdvancedSearch'
import SearchBarAutoCompleterNew from './AutoCompleter/SearchBarAutoCompleterNew'


const Dimmer = ({
  isDimmed,
  node,
}: {
  isDimmed: boolean
  node?: HTMLElement
}): React.ReactElement | null =>
  isDimmed && node
    ? ReactDOM.createPortal(
        <Box
          className="dimmer"
          position="fixed"
          h="100vh"
          w="100vw"
          top="0"
          left="0"
          bgColor="blackAlpha.600"
          zIndex="overlay"
        />,
        node,
      )
    : null

interface Props {
  searchFocused: boolean
  setSearchFocused(val: boolean): void
}

const Search = ({
  searchFocused,
  setSearchFocused,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const [state, dispatch] = useContext(MLContext)
  const {
    savedSearches: contextSavedSearches,
  } = useContext(NavBarContext)
  const history = useHistory()

  /* ************************  STATE ******************** */
  const [searchMode, setSearchMode] = useState<string | null>(state.searchMode)
  const [searchInputMode, setSearchInputMode] = useState<string>(
    state.searchInputMode,
  )
  const [searchByTags, setSearchByTags] = useState<boolean>(
    state.searchByTags ?? true,
  )
  const [fileType, setFileType] = useState<FileTypeStateObject>({
    text: !isEmpty(state.fileType) ? t(fileTypeOptionsMapping[state.fileType]) : '',
    value: state.fileType,
  })
  const [versionIncluded, setVersionIncluded] = useState<boolean>(
    state.isVersionIncludedInSearch ?? false,
  )
  const [searchQueryJson, setSearchQueryJson] = useState<SearchQueryJson>(
    state.searchQueryJson,
  )
  const [searchQuery, setSearchQuery] = useState<string>(
    state.searchQuery || '',
  )
  const [inputBoxText, setInputBoxText] = useState<string | undefined>()

  // Navigation state for auto-completer
  const [isNavigatingToDetail, setIsNavigatingToDetail] = useState<boolean>(false)

  // State to control whether to reset search when opening semantic image search
  const [shouldReset, setShouldReset] = useState<boolean>(true)

  // Ref for max width calculation (parent container for AdvancedSearch)
  const maxWidthRef = useRef<HTMLDivElement>(null)

  // Track the last values we dispatched to avoid re-dispatching same values
  const lastDispatchedRef = useRef({
    searchQuery: state.searchQuery,
    searchQueryJson: state.searchQueryJson,
    searchMode: state.searchMode,
    fileType: state.fileType,
    isVersionIncludedInSearch: state.isVersionIncludedInSearch,
    searchByTags: state.searchByTags,
  })

  // Save search modal state
  const {
    isOpen: isSaveSearchModalOpen,
    onOpen: onOpenSaveSearchModal,
    onClose: onCloseSaveSearchModal,
  } = useDisclosure()

  /* ************************  STATE END ******************** */

  /* ***************** CUSTOM METADATA FIELDS FETCH ****************** */
  let { cmFields: customMetadataFields } = useContext(CustomMetadataContext)

  customMetadataFields = customMetadataFields.map(x => ({
    ...x,
    name: `"customMetadata.${x.name}"`,
  }))

  const customMetadataFieldsMapping: {
    [fieldName: string]: CustomMetadataResponse
  } = useMemo(() => {
    const ret: {
      [fieldName: string]: CustomMetadataResponse
    } = {}
    customMetadataFields.forEach(customMetadataField => {
      ret[customMetadataField.name] = customMetadataField
    })
    return ret
  }, [customMetadataFields])
  /* ************************* CUSTOM METADATA FIELDS FETCH END ********************* */

  /* ****************** CONSTANTS **************** */
  // inputBoxLeftElement with AI mode tags
  const inputBoxLeftElement = useMemo<React.ReactElement>(() => {
    // AI Text mode - show magnifying glass + AI POWERED tag
    if (searchMode === 'ai-text') {
      return (
        <HStack spacing="0" width="auto">
          <Center width="2.5rem">
            <Icon color="#9DA3AE" boxSize="1.25rem" as={IoSearch} />
          </Center>
          <Tag padding="0.3em" marginLeft="0.5em" bg="#0450d5">
            <TagLabel textColor="white">AI POWERED</TagLabel>
          </Tag>
        </HStack>
      )
    }

    // AI Image mode - show magnifying glass + image preview + Searching by image tag
    if (searchMode === 'ai-image') {
      return (
        <HStack spacing="0" width="auto">
          <Center width="2.5rem">
            <Icon color="#9DA3AE" boxSize="1.25rem" as={IoSearch} />
          </Center>
          <Tag
            padding="0.3em"
            bg={state.fileListError ? 'appVeryLightPink' : '#0450d5'}
          >
            {state.fileListError ? (
              <Icon as={InfoIcon} color="appWatermelon" boxSize="14px" mr="0.5em" />
            ) : (
              state.q && (
                <Box
                  h="16px"
                  w="16px"
                  backgroundImage={`url(${state.q})`}
                  backgroundPosition="center"
                  backgroundSize="cover"
                  backgroundRepeat="no-repeat"
                  borderRadius="2px"
                  mr="0.5em"
                  border="1px solid #fff"
                />
              )
            )}
            <TagLabel textColor={state.fileListError ? 'appWatermelon' : 'white'}>
              {t('searching_by_image', 'Searching by image')}
            </TagLabel>
          </Tag>
        </HStack>
      )
    }

    // Default: just magnifying glass
    return (
      <Center width="2.5rem">
        <Icon color="#9DA3AE" boxSize="1.25rem" as={IoSearch} />
      </Center>
    )
  }, [searchMode, state.q, state.fileListError, t, setSearchFocused, dispatch])

  // Simplified inputBoxRightElement - clear button (when state.q exists) + semantic search
  const inputBoxRightElement = useMemo<React.ReactElement | null>(
    () => (
      <Flex align="center">
        {/* Clear button - only show when state.q exists */}
        {state.q && (
          <IconButton
            aria-label="Clear"
            icon={<TbX />}
            variant="outline"
            border="none"
            borderRadius="0"
            colorScheme="#a0aec0"
            fontSize="1.25em"
            onClick={(event) => {
              event.preventDefault()
              dispatch([
                { type: MediaLibraryActions.SET_Q, payload: '' },
                {
                  type: MediaLibraryActions.SET_SEARCH_MODE,
                  payload: 'keyword',
                },
                { type: MediaLibraryActions.RESET_MIN_SCORE }
              ])
            }}
          />
        )}
        {/* Semantic search button - always visible if user has permission */}
        {user?.pricingPlan.featuresList.includes(
          FeaturesEnum.SEMANTIC_SEARCH,
        ) && (
          <IconButton
            aria-label="Visual Search"
            icon={<TbPhotoSearch />}
            variant="outline"
            border="none"
            borderRadius="0"
            borderColor={colors.appPaleLilac}
            colorScheme="#a0aec0"
            fontSize="1.25em"
            onClick={() => {
              setSearchFocused(true)
              dispatch([
                {
                  type: MediaLibraryActions.SET_OPEN_SEMANTIC_IMAGE_SEARCH,
                  payload: true,
                },
              ])
            }}
          />
        )}
      </Flex>
    ),
    [
      state.q,
      state.openSemanticImageSearch,
      user?.pricingPlan.featuresList,
      dispatch,
    ],
  )

  const searchInputRef = useRef(null)

  const searchInputActualRef = useRef<HTMLInputElement>(null)
  const inputRightElementRef = useRef<HTMLDivElement>(null)

  const { url } = useRouteMatch()

  // Debounce timeout ref
  const dispatchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      if (dispatchTimeoutRef.current) {
        clearTimeout(dispatchTimeoutRef.current)
        dispatchTimeoutRef.current = null
      }
    },
    [],
  )

  // Calculate dynamic padding based on right element width
  useEffect(() => {
    if (inputRightElementRef.current && inputBoxRightElement && searchInputActualRef.current) {
      const width = inputRightElementRef.current.offsetWidth
      // Set the CSS custom property directly on the input element
      searchInputActualRef.current.style.setProperty('--right-width', `${width + 8}px`)
    }
  }, [inputBoxRightElement])

  const validationObject: FormValidationObject = useMemo(() => {
    let isValid = true
    const errors: Array<Array<string | null>> = []

    searchQueryJson.filterGroups.forEach((filterGroup, groupIndex) => {
      errors.push([])
      filterGroup.expressions.forEach((expression, filterIndex) => {
        const validation = validateFilter({
          expression,
          customMetadataField:
            customMetadataFieldsMapping[expression.fieldName],
        })
        if (!validation.isValid) {
          isValid = false
        }
        errors[groupIndex][filterIndex] = validation.error
      })
    })

    return {
      isValid,
      errors,
    }
  }, [searchQueryJson, customMetadataFieldsMapping])
  /* ****************** CONSTANTS END **************** */

  /* ************************* USER-ONLY WRAPPERS + GUARDS ********************** */

  const userDirtyRef = useRef(false)

  const setSearchQueryJsonUser = (val: SearchQueryJson) => {
    userDirtyRef.current = true
    setSearchQueryJson(val)
  }
  
  const setSearchModeUser = (val: string | null) => {
    userDirtyRef.current = true
    setSearchMode(val)
  }
  
  const setSearchByTagsUser = (val: boolean) => {
    userDirtyRef.current = true
    setSearchByTags(val)
  }
  
  const setVersionIncludedUser = (val: boolean) => {
    userDirtyRef.current = true
    setVersionIncluded(val)
  }
  
  const setFileTypeUser = (opt: { text: string | ""; value: FileType | "" }) => {
    userDirtyRef.current = true
    setFileType(opt)
  }

  // Build payload from local draft
  const buildPayload = (searchInputMode: string) => {
    if (searchInputMode === 'query') {
      return {
        searchQueryJson,
        searchQuery: canonicalize(searchQuery),
        searchMode: (searchMode ?? 'keyword'),
        fileType: fileType.value,
        isVersionIncludedInSearch: versionIncluded,
        searchByTags: searchByTags,
      }
    }
    const currentSearchQuery = getSearchQueryFromJSON({
      searchQueryJson,
      customMetadataFieldsMapping,
    })
    return {
      searchQueryJson,
      searchQuery: canonicalize(currentSearchQuery),
      searchMode: (searchMode ?? 'keyword'),
      fileType: fileType.value,
      isVersionIncludedInSearch: versionIncluded,
      searchByTags: searchByTags,
    }
  }

  /* ************************* CALLBACKS END ********************** */

  /* ************************ EFFECTS ************************ */

  useEffect(() => {
    const onClose = () => {
      void setSearchFocused(false)
      dispatch([
        {
          type: MediaLibraryActions.SET_OPEN_SEMANTIC_IMAGE_SEARCH,
          payload: false,
        },
        {
          type: MediaLibraryActions.SET_DISABLE_DETAIL_NAVIGATION,
          payload: false,
        },
      ])
    }
    window.addEventListener('mlSearchAutocomplete:close', onClose)
    return () =>
      void window.removeEventListener('mlSearchAutocomplete:close', onClose)
  }, [setSearchFocused])

  useEffect(() => {
    const onOpen = () => {
      void setSearchFocused(true)
    }
    window.addEventListener('mlSearchAutocomplete:open', onOpen)
    return () =>
      void window.removeEventListener('mlSearchAutocomplete:open', onOpen)
  }, [setSearchFocused])

  useEffect(() => {
    if (searchMode === 'ai-image') {
      setShouldReset(false)
    }
    else {
      setShouldReset(true)
    }
  }, [searchMode, searchFocused])


  useEffect(() => {
    if (userDirtyRef.current) {
      return
    }

    setSearchQuery(state.searchQuery)
    setSearchMode(state.searchMode)
    setFileType({
      text: !isEmpty(state.fileType) ? t(fileTypeOptionsMapping[state.fileType]) : "",
      value: state.fileType,
    })
    setSearchInputMode(state.searchInputMode)
    setSearchByTags(state.searchByTags ?? true)
    setVersionIncluded(state.isVersionIncludedInSearch ?? false)
    setSearchQueryJson(state.searchQueryJson)

    // Update lastDispatchedRef to match current global state
    // This prevents user-change effect from thinking there's a difference
    lastDispatchedRef.current = {
      searchQuery: state.searchQuery,
      searchQueryJson: state.searchQueryJson,
      searchMode: state.searchMode,
      fileType: state.fileType,
      isVersionIncludedInSearch: state.isVersionIncludedInSearch,
      searchByTags: state.searchByTags,
    }
  }, [
    state.fileType,
    state.searchMode,
    state.searchInputMode,
    state.searchByTags,
    state.isVersionIncludedInSearch,
    state.searchQuery,
    state.searchQueryJson,
    state.searchQuery,
    t,
  ])

  const stringifiedSearchQueryJson = useMemo(() => {
    return JSON.stringify(searchQueryJson)
  }, [searchQueryJson])

  const validationObjectString = useMemo(() => {
    return JSON.stringify(validationObject)
  }, [validationObject])

  useEffect(() => {
    if (!userDirtyRef.current) {
      return
    }

    if (!validationObject.isValid) {
      return
    }

    const payload = buildPayload(searchInputMode)

    // Compare against LAST DISPATCHED values, not current global state
    // This prevents double dispatch when global state updates from our own dispatch
    const hasChanges =
      payload.searchQuery !== lastDispatchedRef.current.searchQuery ||
      !isEqual(
        payload.searchQueryJson,
        lastDispatchedRef.current.searchQueryJson,
      ) ||
      payload.searchMode !== lastDispatchedRef.current.searchMode ||
      payload.fileType !== lastDispatchedRef.current.fileType ||
      payload.isVersionIncludedInSearch !==
        lastDispatchedRef.current.isVersionIncludedInSearch ||
      payload.searchByTags !== lastDispatchedRef.current.searchByTags

    // If nothing actually changed from last dispatch, just mark clean
    if (!hasChanges) {
      userDirtyRef.current = false
      return
    }

    // Build actions array
    const actions: any[] = []

    if (
      !isEqual(
        payload.searchQueryJson,
        lastDispatchedRef.current.searchQueryJson,
      )
    ) {
      actions.push({
        type: MediaLibraryActions.SET_SEARCH_QUERY_JSON,
        payload: payload.searchQueryJson,
      })
    }

    if (payload.searchQuery !== lastDispatchedRef.current.searchQuery) {
      actions.push({
        type: MediaLibraryActions.SET_SEARCH_QUERY,
        payload: payload.searchQuery,
      })
    }

    if (payload.searchMode !== lastDispatchedRef.current.searchMode) {
      actions.push({
        type: MediaLibraryActions.SET_SEARCH_MODE,
        payload: payload.searchMode,
      })
    }

    if (payload.fileType !== lastDispatchedRef.current.fileType) {
      actions.push({
        type: MediaLibraryActions.SET_SEARCH_FILE_TYPE,
        payload: payload.fileType,
      })
    }

    if (
      payload.isVersionIncludedInSearch !==
      lastDispatchedRef.current.isVersionIncludedInSearch
    ) {
      actions.push({
        type: MediaLibraryActions.SET_IS_VERSION_INCLUDED_IN_SEARCH,
        payload: payload.isVersionIncludedInSearch,
      })
    }

    if (payload.searchByTags !== lastDispatchedRef.current.searchByTags) {
      actions.push({
        type: MediaLibraryActions.SET_SEARCH_BY_TAGS,
        payload: payload.searchByTags,
      })
    }

    // Debounced dispatch
    if (actions.length > 0) {
      // Clear any pending timeout
      if (dispatchTimeoutRef.current) {
        clearTimeout(dispatchTimeoutRef.current)
      }

      dispatchTimeoutRef.current = setTimeout(() => {
        // Dispatch actions
        dispatch(actions)

        // Update lastDispatchedRef with new values
        lastDispatchedRef.current = {
          searchQuery: payload.searchQuery,
          searchQueryJson: payload.searchQueryJson,
          searchMode: payload.searchMode,
          fileType: payload.fileType,
          isVersionIncludedInSearch: payload.isVersionIncludedInSearch,
          searchByTags: payload.searchByTags,
        }

        // Mark as clean
        userDirtyRef.current = false
        dispatchTimeoutRef.current = null
      }, 500)
    } else {
      userDirtyRef.current = false
    }
  }, [
    stringifiedSearchQueryJson,
    validationObjectString,
    fileType.value,
    searchMode,
    searchByTags,
    versionIncluded,
    searchQuery,
    searchInputMode,
  ])
  /* ************************ EFFECTS END ************************ */

  // Input placeholder text logic
  const getInputBoxText = useCallback((): string => {
    // Hide text and placeholder when in ai-image mode
    if (searchMode === 'ai-image') {
      return ''
    }
    if (state.q !== '') {
      return state.q
    }
    if (searchInputMode === 'query' && searchQuery) {
      return t('custom_search_filters_capitalized', 'Custom search filters')
    }
    return t('start_typing_to_search', 'Start typing to search')
  }, [state.q, searchInputMode, searchMode, t, searchQuery, searchQueryJson])

  useEffect(() => {
    setInputBoxText(getInputBoxText())
  }, [getInputBoxText])

  // Generate URL for saving search
  const generateUrlForSaveSearch = useCallback(() => {
    const query: Record<string, string> = {}

    if (searchByTags === false) {
      query.searchByTags = String(searchByTags)
    }

    if (versionIncluded) {
      query.isVersionIncludedInSearch = String(versionIncluded)
    }

    // Calculate the current search query from LOCAL searchQueryJson
    const currentSearchQuery =
      searchInputMode === 'query'
        ? searchQuery
        : getSearchQueryFromJSON({
            searchQueryJson,
            customMetadataFieldsMapping,
          })

    // Include searchMode unless searchInputMode is 'query'
    if (state.q.trim().length > 0 && searchMode && searchInputMode !== 'query') {
      query.searchMode = searchMode
    }

    if (searchInputMode && searchInputMode !== 'builder') {
      query.searchInputMode = searchInputMode
    }

    if (fileType.value.trim().length > 0) {
      query.fileType = fileType.value
    }

    if (state.q.trim()) {
      query.q = state.q
    }

    // Use the computed currentSearchQuery instead of searchQuery
    if (currentSearchQuery.trim()) {
      query.searchQuery = currentSearchQuery
    }

    const qs = stringify(query, { skipNull: true })
    return `${window.location.origin}/search?${qs}`
  }, [
    state.q,
    searchQuery,
    stringifiedSearchQueryJson,
    searchMode,
    searchInputMode,
    searchByTags,
    versionIncluded,
    fileType,
    customMetadataFieldsMapping,
  ])

  // Check if the current search is already saved
  const isSearchAlreadySaved = useMemo(() => {
    const urlToSave = generateUrlForSaveSearch()
    if (!contextSavedSearches || contextSavedSearches.length === 0) {
      return false
    }

    const normalizedCurrentUrl = normalizeSearchUrlForComparison(
      urlToSave,
      customMetadataFields,
    )

    if (!normalizedCurrentUrl) {
      return false
    }

    return contextSavedSearches.some(savedSearch => {
      const normalizedSavedUrl = normalizeSearchUrlForComparison(
        savedSearch.url,
        customMetadataFields,
      )
      return normalizedSavedUrl === normalizedCurrentUrl
    })
  }, [
    contextSavedSearches,
    state.q,
    searchQuery,
    searchMode,
    searchInputMode,
    searchByTags,
    customMetadataFields,
    versionIncluded,
    generateUrlForSaveSearch,
  ])

  const disableSaveSearch = useMemo(() => {
    return (
      (!state.q && !searchQuery && fileType.value.trim().length === 0 && versionIncluded === false) ||
      !validationObject.isValid ||
      isSearchAlreadySaved
    )
  }, [state.q, searchQuery, fileType.value, versionIncluded, validationObject.isValid, isSearchAlreadySaved])

  // Tooltip message for save search button
  const saveSearchTooltipMessage = useMemo(() => {
    if (isSearchAlreadySaved) {
      return 'Saved search is already present'
    }
    if (!validationObject.isValid) {
      return 'Invalid search - please fix validation errors'
    }
    if (
      !state.q &&
      !searchQuery &&
      fileType.value.trim().length === 0 &&
      !versionIncluded
    ) {
      return 'Please enter a search query'
    }
    return ''
  }, [
    isSearchAlreadySaved,
    validationObject.isValid,
    state.q,
    searchQuery,
    fileType.value,
    versionIncluded,
  ])

  if (!customMetadataFields) {
    return <></>
  }

  return (
    <>
      <VStack align={"start"}>
        <Flex width="full">
          <HStack
            ref={searchInputRef}
            spacing="0"
            cursor="pointer"
            zIndex={1401}
            height="35px"
            width="600px"
            bgColor={colors.appWhite}
            paddingRight="14px"
          >
            <InputGroup
              ref={searchInputActualRef}
            >
              <InputLeftElement
                children={inputBoxLeftElement}
                paddingLeft="0.5em"
                position="static"
                width="unset"
                borderWidth="1px"
                borderColor="#e6e7ea"
                borderRadius="0.25rem"
                borderRightWidth="0"
                borderRightRadius="0"
                cursor="default"
              />
              <Input
                ref={searchInputActualRef}
                isReadOnly
                cursor="pointer"
                placeholder={inputBoxText}
                value={undefined}
                focusBorderColor="none"
                _hover={{ borderColor: '#e6e7ea' }}
                borderLeftRadius="0"
                borderLeftWidth="0"
                position="static"
                paddingLeft="0.5em"
                paddingRight={inputBoxRightElement ? "var(--right-width, 0.5em)" : "0.5em"}
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                overflow="hidden"
                onClick={() => void setSearchFocused(true)}
              />
              {inputBoxRightElement ? (
                <InputRightElement
                  ref={inputRightElementRef}
                  width="unset"
                  children={inputBoxRightElement}
                />
              ) : null}
            </InputGroup>
          </HStack>

          {url === '/search' && (
          <HStack spacing="4">
            <Button
              variant="link"
              fontSize="14px"
              colorScheme="gray"
              fontWeight="500"
              onClick={() => {
                dispatch([
                  { type: MediaLibraryActions.SET_Q, payload: '' },
                  { type: MediaLibraryActions.SET_SEARCH_QUERY, payload: '' },
                  {
                    type: MediaLibraryActions.SET_SEARCH_QUERY_JSON,
                    payload: { boolean: 'and', filterGroups: [{
                      expressions: [],
                      boolean: "and",
                    }] },
                  },
                  {
                    type: MediaLibraryActions.SET_SEARCH_BY_TAGS,
                    payload: true,
                  },
                  {
                    type: MediaLibraryActions.SET_IS_VERSION_INCLUDED_IN_SEARCH,
                    payload: false,
                  },
                  {
                    type: MediaLibraryActions.SET_SEARCH_FILE_TYPE,
                    payload: "",
                  },
                  {
                    type: MediaLibraryActions.SET_SEARCH_MODE,
                    payload: 'keyword',
                  },
                  {
                    type: MediaLibraryActions.SET_SEARCH_INPUT_MODE,
                    payload: 'builder',
                  },
                  {
                    type: MediaLibraryActions.SET_SORT,
                    payload: `${MLSortDirection.DESC}_${MLSortParams.CREATED}`,
                  },
                  { type: MediaLibraryActions.RESET_MIN_SCORE }
                ])
              }}
            >
              Reset search
            </Button>
            <Tooltip
              label={saveSearchTooltipMessage}
              isDisabled={!saveSearchTooltipMessage}
              shouldWrapChildren
              hasArrow
            >
              <Button
                variant="link"
                fontSize="14px"
                colorScheme="appBlue"
                fontWeight="500"
                isDisabled={disableSaveSearch}
                onClick={() => {
                  void onOpenSaveSearchModal()
                }}
              >
                Save search
              </Button>
            </Tooltip>
          </HStack>
          )}
        </Flex>

        {url === '/search' && (
          <Box ref={maxWidthRef} width="full">
            <AdvancedSearch
              searchByTags={searchByTags}
              setSearchByTags={setSearchByTagsUser}
              isVersionIncludedInSearch={versionIncluded}
              setIsVersionIncludedInSearch={setVersionIncludedUser}
              searchQueryJson={searchQueryJson}
              setSearchQueryJson={setSearchQueryJsonUser}
              customMetadataFields={customMetadataFields}
              customMetadataFieldsMapping={customMetadataFieldsMapping}
              validationObject={validationObject}
              exposeVersionSettingOnly={searchInputMode === "query"}
              fileType={fileType}
              setFileType={setFileTypeUser}
              maxWidthRef={maxWidthRef}
            />
          </Box>
        )}

        {(searchFocused || isNavigatingToDetail) && (
          <SearchBarAutoCompleterNew
            isNavigatingToDetail={isNavigatingToDetail}
            setIsNavigatingToDetail={setIsNavigatingToDetail}
            shouldReset={shouldReset}
            setShouldReset={setShouldReset}
          />
        )}
      </VStack>

      <Dimmer node={document.body} isDimmed={searchFocused} />

      {/* Save Search Modal */}
      <SaveSearchModalNew
        isOpen={isSaveSearchModalOpen}
        onClose={onCloseSaveSearchModal}
        url={generateUrlForSaveSearch()}
        onSaveSuccess={() => {
          void onCloseSaveSearchModal()
        }}
      />
    </>
  )
}

export default Search
